/**
 * Google Analytics 4 (gtag.js) — marketing property G-JZHQYB0GBC.
 *
 * Loaded on every production visit via GoogleAnalyticsLoader using Google
 * Consent Mode v2: analytics_storage defaults to "denied" (no cookies, but
 * cookieless pings still reach GA4 so traffic is never lost), and is upgraded
 * to "granted" once the user accepts analytics cookies.
 *
 * SPA route changes are tracked automatically by patching the History API —
 * this app navigates with history.pushState (no react-router), so the default
 * gtag page_view (which only fires on hard loads) would otherwise miss every
 * in-app navigation.
 */

export const GA_MEASUREMENT_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GA_MEASUREMENT_ID) ||
  'G-JZHQYB0GBC';

/** Google Ads conversion account + purchase conversion label (Cashfree payment success). */
export const GOOGLE_ADS_CONVERSION_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_ADS_ID) ||
  'AW-18229147357';
export const GOOGLE_ADS_PURCHASE_LABEL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_ADS_PURCHASE_LABEL) ||
  '5QW1CJfy69wcEN3tqvRD';

const IS_PROD =
  typeof import.meta !== 'undefined' &&
  (import.meta.env?.VITE_APP_ENV === 'production' || import.meta.env?.PROD === true);

let gaInitStarted = false;
let routeTrackingStarted = false;
let lastTrackedPath = '';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const LOCATION_CHANGE_EVENT = 'ggj:locationchange';

/**
 * Inject gtag.js immediately with Consent Mode v2.
 * @param opts.analyticsGranted whether the user has already consented to analytics cookies.
 */
export function initGoogleAnalytics(opts?: { analyticsGranted?: boolean }): void {
  if (!IS_PROD || gaInitStarted || typeof window === 'undefined') return;
  gaInitStarted = true;

  const bootstrapped = !!(window as { __GGJ_GTAG_BOOTSTRAPPED__?: boolean }).__GGJ_GTAG_BOOTSTRAPPED__;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  if (!bootstrapped) {
    // Consent Mode v2 — denied by default. GA4 still records cookieless,
    // privacy-safe pings, so traffic counts even before the user accepts cookies.
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: opts?.analyticsGranted ? 'granted' : 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });

    window.gtag('js', new Date());
    // send_page_view: false — we fire page_view manually so SPA navigations are
    // counted exactly once (see startRouteTracking).
    window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
    // Register the Google Ads account so gtag('event','conversion',{send_to:'AW-…'})
    // is attributed correctly. Reuses the same gtag.js already loaded for GA4.
    window.gtag('config', GOOGLE_ADS_CONVERSION_ID);

    const existing = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`,
    );
    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.insertBefore(script, document.head.firstChild);
    }
  } else if (opts?.analyticsGranted) {
    setAnalyticsConsent(true);
  }

  startRouteTracking();
}

/** Upgrade or downgrade analytics consent after the user chooses in the cookie banner. */
export function setAnalyticsConsent(granted: boolean): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

/**
 * Update all consent types based on user's cookie preferences.
 * Call this when the user clicks Accept All, Reject All, or Save Preferences.
 */
export function updateGtagConsent(prefs: {
  analytics?: boolean;
  marketing?: boolean;
}): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage: prefs.marketing ? 'granted' : 'denied',
    ad_user_data: prefs.marketing ? 'granted' : 'denied',
    ad_personalization: prefs.marketing ? 'granted' : 'denied',
  });
}

/** Track a page view. Defaults to the current URL; dedupes consecutive identical paths. */
export function gtagPageView(path?: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const page = path ?? `${window.location.pathname}${window.location.search}`;
  if (page === lastTrackedPath) return;
  lastTrackedPath = page;
  window.gtag('event', 'page_view', {
    page_path: page,
    page_location: window.location.href,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  });
}

/**
 * Patch the History API + popstate so a page_view fires on every SPA navigation.
 * Idempotent — safe to call more than once. Also fires the initial page view.
 */
export function startRouteTracking(): void {
  if (routeTrackingStarted || typeof window === 'undefined') return;
  routeTrackingStarted = true;

  const fire = () => gtagPageView();

  const patch = (type: 'pushState' | 'replaceState') => {
    const original = history[type];
    history[type] = function (this: History, ...args: unknown[]) {
      const result = (original as (...a: unknown[]) => unknown).apply(this, args);
      window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
      return result;
    } as typeof original;
  };
  patch('pushState');
  patch('replaceState');

  window.addEventListener('popstate', fire);
  window.addEventListener(LOCATION_CHANGE_EVENT, fire);

  // Initial page view for the first (hard) load.
  fire();
}

/** Track a custom event. */
export function gtagEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params ?? {});
}

/**
 * Fire the Google Ads purchase conversion after a payment is confirmed.
 * No-op unless gtag.js is loaded (production + analytics initialized).
 * @param opts.value order value in rupees (INR)
 * @param opts.transactionId order/subscription id — dedupes repeat page loads
 */
export function trackGoogleAdsConversion(opts?: {
  value?: number;
  currency?: string;
  transactionId?: string;
}): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const params: Record<string, unknown> = {
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
  };
  if (opts?.value != null && Number.isFinite(opts.value)) params.value = opts.value;
  if (opts?.currency) params.currency = opts.currency;
  if (opts?.transactionId) params.transaction_id = opts.transactionId;
  window.gtag('event', 'conversion', params);
}

/**
 * Fire the standard GA4 ecommerce purchase event after payment is confirmed.
 * Mark as a Key event in GA4 Admin once it appears in the Events list.
 */
export function trackGa4Purchase(opts?: {
  value?: number;
  currency?: string;
  transactionId?: string;
  itemName?: string;
}): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const params: Record<string, unknown> = {
    currency: opts?.currency ?? 'INR',
  };
  if (opts?.value != null && Number.isFinite(opts.value)) params.value = opts.value;
  if (opts?.transactionId) params.transaction_id = opts.transactionId;
  if (opts?.itemName) {
    params.items = [{ item_name: opts.itemName, quantity: 1 }];
  }
  window.gtag('event', 'purchase', params);
}

/**
 * Fire GA4 subscribe event after subscription mandate is confirmed.
 * Use a distinct event name so GA4 / Google Ads can separate subscriptions
 * from one-time credit-pack purchases (both land on /payment-success).
 */
export function trackGa4Subscribe(opts?: {
  value?: number;
  currency?: string;
  transactionId?: string;
  planId?: string;
}): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const params: Record<string, unknown> = {
    currency: opts?.currency ?? 'INR',
  };
  if (opts?.value != null && Number.isFinite(opts.value)) params.value = opts.value;
  if (opts?.transactionId) params.transaction_id = opts.transactionId;
  if (opts?.planId) {
    params.items = [{ item_name: opts.planId, item_category: 'subscription', quantity: 1 }];
  }
  window.gtag('event', 'subscribe', params);
}
